import React from "react";

import { ClientFactory } from "@/main/common/ClientFactory";
import type { BoardCardSummary } from "@/main/react/board/boardView";

/**
 * One summaries request per feed PAGE, not per card — design §18.2.
 *
 * <p><b>Why a batcher instead of a prop.</b> A discussion card is rendered by nine different lists (home,
 * group, series, search, profile, reading history, …), and threading a board payload down each of them
 * would be nine changes plus nine chances to forget the tenth. Cards ask for their own id here; this
 * module coalesces every ask that lands in the same tick into a single call. `DiscussionCardDisplayer` is
 * the only component that changes, and every list inherits the behaviour.
 *
 * <p><b>It is deliberately NOT a `useSyncExternalStore`.</b> Rule 31's hazard is a shared snapshot whose
 * identity changes on a write that changed nothing, re-rendering every subscriber. Here each card holds
 * its own summary in its own `useState` and is notified once, so a board card cannot re-render a
 * board-less sibling at all. The cache below is a plain resolution record, never an observed snapshot.
 *
 * <p><b>Absence is cached as hard as presence.</b> Most posts are not boards, so `null` is the common
 * answer and re-asking for it on every scroll would turn a rare feature into a per-card request. A key
 * present in {@link resolved} means "answered", whatever the answer was.
 *
 * <p>The failure posture is silence: a rejected batch resolves every id in it to `null`, the cards render
 * exactly as they did before boards existed, and nothing retries. A feed card is not worth a retry storm,
 * and rule 32's client-side form says the block may only ADD to a card that is already complete.
 */

/** Answered ids. A `null` value means "asked, and this post has no board". */
const resolved = new Map<string, BoardCardSummary | null>();

/** Ids asked for but not yet flushed. */
let pending = new Set<string>();

/** Callbacks waiting on an id, so one resolution can notify every card showing that post. */
const waiters = new Map<string, Set<(summary: BoardCardSummary | null) => void>>();

let flushHandle: ReturnType<typeof setTimeout> | null = null;

/**
 * Matches `BoardCardSummaryDTO.MAX_SUMMARIES_PER_REQUEST`. A page is 20, so this is headroom rather than
 * a limit the product reaches; asking for more than the server will answer would silently drop the tail.
 */
const MAX_IDS_PER_REQUEST = 50;

function notify(id: string, summary: BoardCardSummary | null) {
    resolved.set(id, summary);
    const listeners = waiters.get(id);
    waiters.delete(id);
    listeners?.forEach((listener) => listener(summary));
}

async function flush() {
    flushHandle = null;
    const ids = [...pending].slice(0, MAX_IDS_PER_REQUEST);
    const overflow = [...pending].slice(MAX_IDS_PER_REQUEST);
    pending = new Set(overflow);
    if (ids.length === 0) {
        return;
    }
    // Anything past the cap goes in the next batch rather than being dropped, which is the difference
    // between "slower" and "some cards silently never get a board".
    if (overflow.length > 0) {
        schedule();
    }

    try {
        const response = await ClientFactory.fetchBoardCardSummaries(ids);
        const payload = response.data ?? {};
        ids.forEach((id) => notify(id, payload[id] ?? null));
    } catch {
        // Silence by design — see the module javadoc. Resolving to null leaves the ordinary card.
        ids.forEach((id) => notify(id, null));
    }
}

function schedule() {
    if (flushHandle === null) {
        flushHandle = setTimeout(() => void flush(), 0);
    }
}

/**
 * The board summary for one post, or null once it is known there is none.
 *
 * <p>Returns `undefined` while unanswered, and callers must render nothing in that state rather than a
 * placeholder: a skeleton on a card that will usually never have a board is a layout shift charged to
 * every post in the feed to serve the rare one.
 */
export function useBoardCardSummary(discussionPublicId: string | undefined | null) {
    const [summary, setSummary] = React.useState<BoardCardSummary | null | undefined>(() =>
        discussionPublicId ? resolved.get(discussionPublicId) : null,
    );

    React.useEffect(() => {
        if (!discussionPublicId) {
            setSummary(null);
            return undefined;
        }
        if (resolved.has(discussionPublicId)) {
            setSummary(resolved.get(discussionPublicId) ?? null);
            return undefined;
        }

        let active = true;
        const listener = (next: BoardCardSummary | null) => {
            if (active) {
                setSummary(next);
            }
        };
        const listeners = waiters.get(discussionPublicId) ?? new Set();
        listeners.add(listener);
        waiters.set(discussionPublicId, listeners);
        pending.add(discussionPublicId);
        schedule();

        return () => {
            active = false;
            waiters.get(discussionPublicId)?.delete(listener);
        };
    }, [discussionPublicId]);

    return summary;
}

/** Test seam only: clears the cache so one spec's answers cannot leak into the next. */
export function resetBoardCardSummariesForTests() {
    resolved.clear();
    pending = new Set();
    waiters.clear();
    if (flushHandle !== null) {
        clearTimeout(flushHandle);
        flushHandle = null;
    }
}
